import React from "react";
import * as dhx from "dhx-suite";

import {
  AppModel,
  Mode,
  SnippetListItem,
  SnippetSearchResult,
} from "../models";
import { Action } from "./actions";
import assertNever from "../utils/assertNever";
import { dtoToSnippet, snippetToDto } from "../services/snippetService";
import history from "../services/history";
import {
  getCurrentMode,
  getFilterParams,
  parseQueryString,
  setSearchingParams,
} from "../utils/queryStringHelper";
import { isMobileDevice } from "../utils/common";

export const initialProductsList = [
  "suite",
  "diagram",
  "spreadsheet",
  "richtext",
  "pivot",
  "vault",
];

const initialSnippet = {
  id: "",
  jsCode: "",
  htmlCode: "",
  title: "",
  tagsIds: [],
  jsSettings: {
    widgets: [],
  },
};

let previewCoolingTimer = 0;

const searchPanelVisibleLocal = localStorage.getItem("searchPanelVisible");
export const initialState: AppModel = {
  snippet: initialSnippet,
  previewCooling: false,
  previewInitialized: false,
  snippedSaveInProgress: false,
  savedSnippet: initialSnippet,
  jsSettingsFormVisible: false,
  searchResults: [],
  initialSnippetsList: [],
  snippetsList: [],
  snippetsFilteredList: [],
  searchPanelVisible:
    searchPanelVisibleLocal === null
      ? !isMobileDevice
      : searchPanelVisibleLocal === true.toString(),
  searchText: "",
  filterText: "",
  filterTags: JSON.parse(JSON.stringify(initialProductsList)),
  tags: new Map<number, string>(),
  darkTheme: localStorage.getItem("darkTheme") === true.toString(),
  mode: "",
  sharePopupVisible: false,
  samplesCount: 1400,
};

function splitWidgetNameAndTitle(title: string) {
  title = title.trim();
  const regex = /^(.*?)(?: (\d+\.\d+\.\d+))?\. (.*)$/;
  const match = title.match(regex);
  if (match) {
    const widgetName = match[1].trim() + (match[2] ? ` ${match[2]}` : "");
    const newTitle = match[3].trim();
    return [widgetName, newTitle];
  }

  return ["", title];
}

function fireResizeEvent() {
  const resizeEvent = window.document.createEvent("UIEvents");
  (resizeEvent as any).initUIEvent("resize", true, false, window, 0);
  window.dispatchEvent(resizeEvent);
}

const suite = [
  "Calendar",
  "Chart",
  "ColorPicker",
  "Combobox",
  "Data",
  "DataView",
  "Form",
  "Helper",
  "Grid",
  "Layout",
  "List",
  "Menu",
  "Message",
  "Pagination",
  "Popup",
  "Ribbon",
  "Sidebar",
  "Slider",
  "Tabbar",
  "Timepicker",
  "Toolbar",
  "Tree",
  "TreeGrid",
  "Window",
];

function filterByTags(state: AppModel) {
  const { initialSnippetsList, filterTags, searchResults, tags } = state;
  if (!filterTags.length || !initialSnippetsList.length)
    return (searchResults || []) as SnippetListItem[];
  const snippetsFilteredList: SnippetListItem[] = [];
  searchResults.forEach((s) => {
    const snippet = initialSnippetsList.find((is) => is.url === s.url);
    if (!snippet) return;
    const tIds = snippet.tagIds.split(",");
    const tagsArr = tIds.map((id) => tags.get(+id));
    if (tagsArr.length < filterTags.length) return;
    for (let i = 0; i < filterTags.length; i++) {
      if (!tagsArr.includes(filterTags[i])) return;
    }
    snippetsFilteredList.push(s as SnippetListItem);
  });
  return snippetsFilteredList;
}

function normalizeFilterList(state: AppModel) {
  const productsFilterList = state.filterTags.filter((i) =>
    initialProductsList.includes(i)
  );
  if (productsFilterList.includes("suite")) {
    productsFilterList.splice(1, 0, ...suite);
  }

  const snippetsListTemp = state.initialSnippetsList.filter((item) => {
    let productCheck: any;
    productsFilterList.forEach((tag) => {
      item.tags.forEach((itemTag) => {
        const entryCheck = RegExp(tag, "i").test(itemTag);
        const indexingCheck = RegExp(tag, "i").exec(itemTag);
        productCheck =
          (entryCheck && indexingCheck && indexingCheck.index === 0) ||
          !!productCheck;
      });
    });

    return productCheck;
  });

  const snippetsList: SnippetListItem[] = [];
  productsFilterList.forEach((widget) => {
    snippetsListTemp.forEach((item) => {
      if (item.widgetName?.toLowerCase() === widget.toLowerCase()) {
        snippetsList.push(item);
      }
    });
  });

  const searchingText = state.filterText
    .replace(/\#\w+/g, "")
    .trim()
    .toLowerCase();
  const snippetsFilteredList = snippetsList.filter((item) => {
    let tagsCheck: any;
    let hasTags = false;
    state.filterText.split(" ").forEach((word) => {
      if (/\#\w+/g.test(word)) {
        item.tags.forEach((tag) => {
          hasTags = true;
          tagsCheck = word.slice(1).toLowerCase() === tag || !!tagsCheck;
        });
      }
    });
    const titleCheck = item.title.toLowerCase().indexOf(searchingText) !== -1;
    const widgetNameCheck = !!(
      item.widgetName &&
      item.widgetName.toLowerCase().indexOf(searchingText) !== -1
    );
    return (titleCheck || widgetNameCheck) && (tagsCheck || !hasTags);
  });

  return { snippetsList, snippetsFilteredList };
}

export function asyncDispatchMiddleware(dispatch: (action: Action) => void) {
  return (action: Action) => {
    switch (action.type) {
      case "SnippetSaveClick": {
        remote.api.snippet
          .Save(snippetToDto(action.snippet))
          .then((id) => {
            history.push(id);
            dispatch({ type: "SnippetSaved", id });
          })
          .catch((error) => dispatch({ type: "SnippetSaveFailed", error }));
        break;
      }
      case "SnippetLoadRequested": {
        remote.api.snippet.Load(action.id).then((snippetDto) => {
          const snippet = dtoToSnippet(snippetDto);
          dispatch({ type: "SnippetLoaded", snippet: snippet });
          if (snippet.description || snippet.buttonHref || snippet.buttonText) {
            setImmediate(fireResizeEvent);
          }
          if (snippet.mode === "wide" && !getCurrentMode()) {
            const qs = parseQueryString(location.href.split("?")[1] || "");
            setSearchingParams(qs.tag, qs.text, "wide");
          }
        });
        break;
      }
      case "SearchPhraseChanged": {
        remote.api.snippet.FindByText(action.text).then((results) => {
          localStorage.setItem("searchListScroll",action.scrollToSnippet ? "scrollToSnippet" : "0");

          const searchResults = results.map((r) => {
            const widgetAndTitle = splitWidgetNameAndTitle(r.title);
            return {
              ...r,
              widgetName: widgetAndTitle[0],
              title: widgetAndTitle[1],
            };
          });
          dispatch({ type: "SnippetSearchFinished", results: searchResults });
        });
        break;
      }
      case "SearchSnippetSelected": {
        dispatch({ type: "JsSettingsWindowCloseClick" });
        const qs = parseQueryString(location.href.split("?")[1] || "");
        // history.push(action.id);
        setSearchingParams(qs.tag, qs.text, qs.mode as Mode,  action.id);
        break;
      }
      case "ListSnippetSelected": {
        dispatch({ type: "JsSettingsWindowCloseClick" });
        window.open(`https://snippet.dhtmlx.com/${action.id}`, "_blank");
        break;
      }
      case "TagClick": {
        localStorage.setItem("searchPanelVisible", "true");
        setImmediate(fireResizeEvent);
        break;
      }
      case "SearchCloseClick": {
        localStorage.setItem("searchPanelVisible", "false");
        setImmediate(fireResizeEvent);
        break;
      }
      case "SearchClick": {
        localStorage.setItem("searchPanelVisible", "true");
        setImmediate(fireResizeEvent);
        break;
      }
      case "SnippetErrorOccured": {
        if (action.msg && action.msg.length) {
          dhx.message({
            text: action.msg,
            css: "st-message__warning",
            icon: "dxi-close",
            expire: 3000,
          });
        }

        break;
      }
      case "HtmlCodeChanged":
      case "JsCodeChanged": {
        if (previewCoolingTimer) {
          window.clearTimeout(previewCoolingTimer);
        }
        previewCoolingTimer = window.setTimeout(() => {
          dispatch({ type: "PreviewCooledDown" });
          previewCoolingTimer = 0;
        }, 1000);

        break;
      }
      case "CopyToClipboardClick": {
        document.execCommand("copy");
        break;
      }
      case "ThemeChanged": {
        localStorage.setItem("darkTheme", action.darkTheme.toString());
        break;
      }
      case "SnippetsListLoad": {
        remote.api.snippet.LoadAll().then((results) => {
          const snippetsList = results.map((r) => {
            const widgetAndTitle = splitWidgetNameAndTitle(r.title);
            return {
              ...r,
              widgetName: widgetAndTitle[0],
              title: widgetAndTitle[1],
            };
          });
          dispatch({ type: "SnippetsListLoadFinished", results: snippetsList });
        });
        break;
      }
      case "GetSamplesCount": {
        remote.api.snippet.FindByText("").then((results) => {
          let count = Math.floor(results.length / 10) * 10;
          dispatch({ type: "SetSamplesCount", count });
        });
        break;
      }
    }
    return dispatch(action);
  };
}

export function reducer(state: AppModel, action: Action): AppModel {
  switch (action.type) {
    case "HtmlCodeChanged": {
      return {
        ...state,
        snippet: { ...state.snippet, htmlCode: action.code },
        scriptErrorLine: undefined,
        scriptErrorMsg: undefined,
        previewCooling: true,
      };
    }
    case "JsCodeChanged": {
      return {
        ...state,
        snippet: { ...state.snippet, jsCode: action.code },
        scriptErrorLine: undefined,
        scriptErrorMsg: undefined,
        previewCooling: true,
      };
    }
    case "JsSettingsButtonClick": {
      return { ...state, jsSettingsFormVisible: !state.jsSettingsFormVisible };
    }
    case "JsSettingsWindowCloseClick": {
      return { ...state, jsSettingsFormVisible: false };
    }
    case "ShareWindowCloseClick": {
      return { ...state, sharePopupVisible: false };
    }
    case "JsSettingsChanged": {
      return {
        ...state,
        snippet: { ...state.snippet, jsSettings: action.settings },
        scriptErrorLine: undefined,
        scriptErrorMsg: undefined,
      };
    }
    case "SnippetSaveClick": {
      return { ...state, snippedSaveInProgress: true };
    }
    case "SnippetNewClick": {
      return {
        ...state,
        snippet: { ...initialSnippet },
        scriptErrorLine: undefined,
        scriptErrorMsg: undefined,
      };
    }
    case "SnippetSaved": {
      return {
        ...state,
        snippedSaveInProgress: false,
        sharePopupVisible: true,
      };
    }
    case "SnippetSaveFailed": {
      return { ...state, snippedSaveInProgress: false };
    }
    case "SnippetLoadRequested": {
      return state;
    }
    case "SnippetLoaded": {
      return {
        ...state,
        snippet: action.snippet,
        savedSnippet: action.snippet,
        scriptErrorLine: undefined,
        scriptErrorMsg: undefined,
      };
    }
    case "TagClick": {
      setSearchingParams([`${action.tag}`], undefined, state.mode);
      return {
        ...state,
        searchText: `#${action.tag}`,
        filterTags: [action.tag],
        searchPanelVisible: true,
      };
    }
    case "SnippetSearchFinished": {
      const { text = "", tag = [] } = getFilterParams();
      const nextState: AppModel = {
        ...state,
        filterTags: tag,
        filterText: text,
        searchResults: action.results,
      };
      const searchResults = filterByTags(nextState);
      return { ...nextState, searchResults };
    }
    case "SearchClick": {
      return { ...state, searchPanelVisible: true };
    }
    case "TagsLoaded": {
      return { ...state, tags: action.tags };
    }
    case "SearchSnippetSelected": {
      return state;
    }
    case "ListSnippetSelected": {
      return state;
    }
    case "SearchPhraseChanged": {
      const searchText = action.text
        ? decodeURI(action.text?.replace("+", " "))
        : "";
      return { ...state, searchText };
    }
    case "SearchCloseClick": {
      return { ...state, searchPanelVisible: false };
    }
    case "JsPanelResized": {
      return {
        ...state,
        jsPanelSize: {
          width: action.size.width,
          height: action.size.height,
        },
      };
    }
    case "HtmlPanelResized": {
      return {
        ...state,
        htmlPanelSize: {
          width: action.size.width,
          height: action.size.height,
        },
      };
    }
    case "SnippetErrorOccured": {
      return {
        ...state,
        scriptErrorLine: action.line,
        scriptErrorMsg: action.msg,
      };
    }
    case "PreviewCooledDown": {
      return state.previewCooling ? { ...state, previewCooling: false } : state;
    }
    case "PreviewInitialized": {
      return { ...state, previewInitialized: true };
    }
    case "ThemeChanged": {
      return { ...state, darkTheme: action.darkTheme };
    }
    case "ModeChanged": {
      return {
        ...state,
        mode: action.mode,
        previewInitialized:
          action.mode === "htmlCode" || action.mode === "jsCode"
            ? false
            : state.previewInitialized,
      };
    }
    case "CopyToClipboardClick": {
      return { ...state, sharePopupVisible: false };
    }
    case "SnippetsListLoad": {
      return { ...state };
    }
    case "SnippetsListLoadFinished": {
      action.results.forEach((item) => {
        const tags = (item as any).tagIds.split(",");
        if (tags.length > 0) {
          item.tags = [];
          tags.forEach((id: string) => {
            const tag = state.tags.get(parseFloat(id)) as string;
            if (tag) {
              item.tags.push(tag);
            }
          });
        }
      });
      const { snippetsList, snippetsFilteredList } = normalizeFilterList({
        ...state,
        snippetsList: action.results,
        initialSnippetsList: action.results,
        snippetsFilteredList: action.results,
      });
      const nextState = {
        ...state,
        snippetsList,
        initialSnippetsList: action.results,
        snippetsFilteredList,
      };
      const filteredByTags = filterByTags(nextState);
      return { ...nextState, searchResults: filteredByTags };
    }
    case "FilterTextChanged": {
      setSearchingParams(undefined, action.text);
      return {
        ...state,
        snippetsFilteredList: normalizeFilterList({
          ...state,
          filterText: action.text,
        }).snippetsFilteredList,
        filterText: action.text,
      };
    }
    case "FilterTagChanged": {
      const tagIndex = state.filterTags.indexOf(action.tag);
      tagIndex !== -1
        ? state.filterTags.splice(tagIndex, 1)
        : state.filterTags.push(action.tag);

      setSearchingParams(state.filterTags);
      const { snippetsList, snippetsFilteredList } = normalizeFilterList(state);
      return { ...state, snippetsList, snippetsFilteredList };
    }
    case "FilterReload": {
      const { snippetsList, snippetsFilteredList } = normalizeFilterList(state);
      return { ...state, snippetsList, snippetsFilteredList };
    }
    case "SetParamsFromQuerySearch": {
      const mode = getCurrentMode();
      const { tag, text } = action;
      let searchText = text ? decodeURI(text?.replace("+", " ")) : "";
      if (tag) {
        tag.forEach((t) => {
          searchText += `#${t} `;
        });
      }
      return { ...state, mode, searchText, filterTags: tag || [] };
    }
    case "SetSamplesCount":
      return { ...state, samplesCount: action.count };
    case "GetSamplesCount":
      return state;
    default:
      return assertNever(action);
  }
}

// @ts-ignore
// tslint:disable-next-line: no-empty
export const DispatchContext = React.createContext<{
  dispatch(action: Action): void;
}>({ dispatch: (_action) => {} });
